import moment from 'moment';
import { createSelector } from 'reselect';
import { selectCurrentUserEducatorSubscription } from '../UserSubscriptionPlans/selectors';
import { SUBSCRIPTION_PLAN_STATUS_TRIAL } from '../UserSubscriptionPlans/constants';
import { selectIsAdminOfCurrentCourse } from '../Courses/selectors';
import { selectIsFacilitatorInCurrentCourse } from '../Enrolments/selectors';
import {
  currentUserIsEducator,
  currentUserSelector,
  selectCurrentUserEducatorStatus,
} from '../Users/selectors';

const educatorSubscriptionsSelector = (state) => state.EducatorSubscriptions;

export const requiresEducatorSubscriptionSelector = createSelector(
  selectIsAdminOfCurrentCourse,
  selectIsFacilitatorInCurrentCourse,
  currentUserSelector,
  (isAdmin, isStaff, currentUser) => {
    return currentUser && (isAdmin || isStaff);
  }
);

export const canDismissSubscribeAsEducatorModalSelector = createSelector(
  educatorSubscriptionsSelector,
  (educatorSubscriptions) =>
    educatorSubscriptions.get('canDismissSubscribeAsEducatorModal', false)
);

const selectEducatorTrialExpiryDate = createSelector(
  selectCurrentUserEducatorSubscription,
  (educatorSubscription) => {
    const expiryDate = educatorSubscription
      ? educatorSubscription.get('expiryDate')
      : null;
    if (expiryDate) {
      const momentDate = moment(expiryDate);
      if (momentDate.isValid()) {
        return momentDate;
      }
    }
    return null;
  }
);

export const daysRemainingInEducatorTrialSelector = createSelector(
  selectEducatorTrialExpiryDate,
  (momentExpiryDate) => {
    let daysRemaining = null;
    // Note: expiryDate could be null if this is an unlimited trial
    if (momentExpiryDate) {
      const today = moment();
      daysRemaining = Math.max(momentExpiryDate.diff(today, 'days'), 0);
    }
    return daysRemaining;
  }
);

export const showTrialCountDownBannerSelector = createSelector(
  requiresEducatorSubscriptionSelector,
  selectCurrentUserEducatorStatus,
  daysRemainingInEducatorTrialSelector,
  (requiresEducatorSubscription, educatorStatus, daysRemaining) => {
    // Due to the timing of the daily task that expires user subscriptions;
    // sometimes it is possible for the expiry date to have lapsed, but the
    // current user's educator status is still 'trial'... in this case,
    // we want to continue displaying the trial banner
    // Therefore, always display the trial banner if the educatorStatus = trial
    // regardless of the expiry date

    // but don't show it until we have loaded the user subscription plan
    // and calculated the daysRemaining, otherwise the banner will flash
    // with the 'about to expire' message before updating with 'N days left'
    return (
      requiresEducatorSubscription &&
      educatorStatus === SUBSCRIPTION_PLAN_STATUS_TRIAL &&
      daysRemaining !== null
    );
  }
);

export const showSubscribeAsEducatorModalSelector = createSelector(
  educatorSubscriptionsSelector,
  requiresEducatorSubscriptionSelector,
  currentUserIsEducator,
  currentUserSelector,
  showTrialCountDownBannerSelector,
  (
    educatorSubscriptions,
    mustBeAnEducator,
    isEducator,
    currentUser,
    hasActiveTrial
  ) =>
    Boolean(
      educatorSubscriptions.get('showSubscribeAsEducatorModal', false) ||
        (currentUser && mustBeAnEducator && !isEducator && !hasActiveTrial)
    )
);
